import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import Card from '../../components/ui/card';
import { useToast } from '../../components/ui/toast';
import SublyApi from '../../helpers/Api';
import DiscoveryModal from './DiscoveryModal';
import { FiKey, FiEdit2, FiCheck, FiX, FiSearch, FiFilter, FiAlertCircle } from 'react-icons/fi';

const LookupKeysManager = () => {
	const [lookupKeys, setLookupKeys] = useState([]);
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState(null);
	const [isDiscoveryModalOpen, setIsDiscoveryModalOpen] = useState(false);
	const [editingKeyId, setEditingKeyId] = useState(null);
	const [editForm, setEditForm] = useState({ name: '', description: '' });
	const [filterActive, setFilterActive] = useState('all'); // 'all', 'active', 'inactive'
	const [searchTerm, setSearchTerm] = useState('');

	const token = useSelector((st) => st.admin.token);
	const { addToast } = useToast();

	// Fetch lookup keys
	const fetchLookupKeys = useCallback(async () => {
		try {
			setIsLoading(true);
			setError(null);
			const header = { Authorization: `Bearer ${token}` };
			const response = await SublyApi.request('api/admin/subscription-lookup-keys', {}, 'get', header);
			setLookupKeys(response.lookupKeys || []);
		} catch (err) {
			console.error('Error fetching lookup keys:', err);
			setError('Failed to load lookup keys');
			addToast({
				title: 'Error',
				description: 'Failed to load lookup keys',
				type: 'error',
			});
		} finally {
			setIsLoading(false);
		}
	}, [token, addToast]);

	useEffect(() => {
		if (token) {
			fetchLookupKeys();
		}
	}, [token, fetchLookupKeys]);

	// Toggle active status
	const handleToggleActive = async (keyId, currentStatus) => {
		try {
			const newStatus = !currentStatus;
			const header = { Authorization: `Bearer ${token}` };
			await SublyApi.request(
				`api/admin/subscription-lookup-keys/${keyId}/toggle`,
				{ is_active: newStatus },
				'patch',
				header
			);

			// Update local state
			setLookupKeys(prevKeys =>
				prevKeys.map(key =>
					key.id === keyId ? { ...key, is_active: newStatus } : key
				)
			);

			addToast({
				title: 'Success',
				description: `Lookup key ${newStatus ? 'activated' : 'deactivated'} successfully`,
				type: 'success',
			});
		} catch (err) {
			console.error('Error toggling lookup key:', err);
			addToast({
				title: 'Error',
				description: 'Failed to update lookup key status',
				type: 'error',
			});
		}
	};

	// Start editing
	const handleStartEdit = (key) => {
		setEditingKeyId(key.id);
		setEditForm({
			name: key.name || '',
			description: key.description || '',
		});
	};

	// Cancel editing
	const handleCancelEdit = () => {
		setEditingKeyId(null);
		setEditForm({ name: '', description: '' });
	};

	// Save edit
	const handleSaveEdit = async (keyId) => {
		try {
			const header = { Authorization: `Bearer ${token}` };
			await SublyApi.request(
				`api/admin/subscription-lookup-keys/${keyId}`,
				editForm,
				'put',
				header
			);

			// Update local state
			setLookupKeys(prevKeys =>
				prevKeys.map(key =>
					key.id === keyId ? { ...key, ...editForm } : key
				)
			);

			setEditingKeyId(null);
			setEditForm({ name: '', description: '' });

			addToast({
				title: 'Success',
				description: 'Lookup key updated successfully',
				type: 'success',
			});
		} catch (err) {
			console.error('Error updating lookup key:', err);
			addToast({
				title: 'Error',
				description: 'Failed to update lookup key',
				type: 'error',
			});
		}
	};

	// Handle discovery completion
	const handleDiscoveryComplete = () => {
		setIsDiscoveryModalOpen(false);
		fetchLookupKeys();
	};

	// Filter and search logic
	const filteredKeys = lookupKeys.filter(key => {
		// Filter by active status
		if (filterActive === 'active' && !key.is_active) return false;
		if (filterActive === 'inactive' && key.is_active) return false;

		// Filter by search term
		if (searchTerm) {
			const searchLower = searchTerm.toLowerCase();
			return (
				key.lookup_key?.toLowerCase().includes(searchLower) ||
				key.name?.toLowerCase().includes(searchLower) ||
				key.description?.toLowerCase().includes(searchLower)
			);
		}

		return true;
	});

	if (isLoading && lookupKeys.length === 0) {
		return (
			<div className="space-y-6">
				<div className="flex justify-between items-center">
					<div className="h-8 bg-gray-200 rounded w-48 animate-pulse"></div>
					<div className="h-10 bg-gray-200 rounded w-40 animate-pulse"></div>
				</div>
				<Card>
					<div className="p-8 space-y-4">
						{[1, 2, 3].map(i => (
							<div key={i} className="h-20 bg-gray-100 rounded animate-pulse"></div>
						))}
					</div>
				</Card>
			</div>
		);
	}

	if (error && lookupKeys.length === 0) {
		return (
			<div className="space-y-6">
				<Card className="p-8">
					<div className="text-center">
						<FiAlertCircle className="mx-auto h-12 w-12 text-red-500 mb-4" />
						<h3 className="text-lg font-montserrat-medium text-gray-900 mb-2">
							Error Loading Lookup Keys
						</h3>
						<p className="text-sm font-montserrat-regular text-gray-600 mb-6">
							{error}
						</p>
						<button
							onClick={fetchLookupKeys}
							className="bg-clientBlack text-white px-6 py-2 rounded font-montserrat-medium text-sm hover:bg-gray-800 transition-colors">
							Try Again
						</button>
					</div>
				</Card>
			</div>
		);
	}

	return (
		<div className="space-y-6">
			{/* Header */}
			<div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
				<div>
					<h2 className="text-2xl font-glacial uppercase text-gray-900">
						Subscription Lookup Keys
					</h2>
					<p className="text-sm font-montserrat-regular text-gray-600 mt-1">
						Manage and sync subscription lookup keys from Stripe
					</p>
				</div>
				<button
					onClick={() => setIsDiscoveryModalOpen(true)}
					className="bg-primary text-white px-6 py-3 rounded font-montserrat-medium text-sm hover:bg-primary/90 transition-colors flex items-center justify-center gap-2 shadow-sm">
					<FiSearch className="h-4 w-4" />
					Discover New Keys
				</button>
			</div>

			{/* Filters and Search */}
			<Card className="p-6">
				<div className="flex flex-col sm:flex-row gap-4">
					{/* Search */}
					<div className="flex-1">
						<div className="relative">
							<FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
							<input
								type="text"
								placeholder="Search lookup keys..."
								value={searchTerm}
								onChange={(e) => setSearchTerm(e.target.value)}
								className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded font-montserrat-regular text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
							/>
						</div>
					</div>

					{/* Filter */}
					<div className="flex items-center gap-2">
						<FiFilter className="h-4 w-4 text-gray-500" />
						<select
							value={filterActive}
							onChange={(e) => setFilterActive(e.target.value)}
							className="border border-gray-300 rounded px-4 py-2 font-montserrat-regular text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary">
							<option value="all">All Keys</option>
							<option value="active">Active Only</option>
							<option value="inactive">Inactive Only</option>
						</select>
					</div>
				</div>
			</Card>

			{/* Results Count */}
			<div className="flex items-center justify-between">
				<p className="text-sm font-montserrat-regular text-gray-600">
					Showing {filteredKeys.length} of {lookupKeys.length} lookup keys
				</p>
				{lookupKeys.length === 0 && (
					<p className="text-sm font-montserrat-medium text-primary">
						No lookup keys found. Click "Discover New Keys" to get started.
					</p>
				)}
			</div>

			{/* Lookup Keys List */}
			<div className="space-y-4">
				{filteredKeys.length === 0 && searchTerm && (
					<Card className="p-8">
						<div className="text-center">
							<p className="text-sm font-montserrat-regular text-gray-600">
								No lookup keys match your search.
							</p>
						</div>
					</Card>
				)}

				{filteredKeys.map((key) => {
					const isEditing = editingKeyId === key.id;

					return (
						<Card key={key.id} className="overflow-hidden hover:shadow-md transition-shadow">
							<div className="p-6">
								<div className="flex flex-col lg:flex-row lg:items-start gap-4">
									{/* Icon */}
									<div className={`rounded-full p-3 ${key.is_active ? 'bg-green-50' : 'bg-gray-50'} flex-shrink-0`}>
										<FiKey className={`h-6 w-6 ${key.is_active ? 'text-green-600' : 'text-gray-400'}`} />
									</div>

									{/* Content */}
									<div className="flex-1 min-w-0">
										{/* Active Toggle */}
										<div className="flex items-center gap-3 mb-3">
											<span className="text-sm font-montserrat-medium text-gray-700">Active:</span>
											<button
												onClick={() => handleToggleActive(key.id, key.is_active)}
												disabled={isEditing}
												className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed ${
													key.is_active ? 'bg-green-500' : 'bg-gray-300'
												}`}
												aria-label={`Toggle ${key.is_active ? 'inactive' : 'active'}`}>
												<span
													className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
														key.is_active ? 'translate-x-6' : 'translate-x-1'
													}`}
												/>
											</button>
										</div>

										{isEditing ? (
											// Edit Mode
											<div className="space-y-3">
												<div>
													<label className="block text-xs font-montserrat-medium text-gray-700 mb-1">
														Name
													</label>
													<input
														type="text"
														value={editForm.name}
														onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
														placeholder="Enter a friendly name..."
														className="w-full border border-gray-300 rounded px-3 py-2 font-montserrat-regular text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
													/>
												</div>
												<div>
													<label className="block text-xs font-montserrat-medium text-gray-700 mb-1">
														Description
													</label>
													<textarea
														value={editForm.description}
														onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
														placeholder="Enter a description..."
														rows={2}
														className="w-full border border-gray-300 rounded px-3 py-2 font-montserrat-regular text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
													/>
												</div>
											</div>
										) : (
											// View Mode
											<div className="space-y-2">
												<h3 className="text-lg font-montserrat-medium text-gray-900">
													{key.name || <span className="text-gray-400 italic">No name set</span>}
												</h3>
												<div className="flex items-center gap-2 mb-2">
													<span className="text-xs font-montserrat-medium text-gray-500">Lookup Key:</span>
													<code className="text-sm font-mono bg-gray-100 px-2 py-1 rounded text-gray-800">
														{key.lookup_key}
													</code>
												</div>
												<p className="text-sm font-montserrat-regular text-gray-600">
													{key.description || <span className="text-gray-400 italic">No description set</span>}
												</p>
												{key.forms_count > 0 && (
													<p className="text-xs font-montserrat-regular text-gray-500">
														Used in {key.forms_count} form{key.forms_count !== 1 ? 's' : ''}
													</p>
												)}
											</div>
										)}
									</div>

									{/* Actions */}
									<div className="flex lg:flex-col gap-2 flex-shrink-0">
										{isEditing ? (
											<>
												<button
													onClick={() => handleSaveEdit(key.id)}
													className="bg-green-600 text-white px-4 py-2 rounded font-montserrat-medium text-sm hover:bg-green-700 transition-colors flex items-center gap-2"
													aria-label="Save changes">
													<FiCheck className="h-4 w-4" />
													Save
												</button>
												<button
													onClick={handleCancelEdit}
													className="bg-gray-200 text-gray-700 px-4 py-2 rounded font-montserrat-medium text-sm hover:bg-gray-300 transition-colors flex items-center gap-2"
													aria-label="Cancel editing">
													<FiX className="h-4 w-4" />
													Cancel
												</button>
											</>
										) : (
											<button
												onClick={() => handleStartEdit(key)}
												className="bg-gray-100 text-gray-700 px-4 py-2 rounded font-montserrat-medium text-sm hover:bg-gray-200 transition-colors flex items-center gap-2"
												aria-label="Edit lookup key">
												<FiEdit2 className="h-4 w-4" />
												Edit
											</button>
										)}
									</div>
								</div>
							</div>
						</Card>
					);
				})}
			</div>

			{/* Discovery Modal */}
			{isDiscoveryModalOpen && (
				<DiscoveryModal
					isOpen={isDiscoveryModalOpen}
					onClose={() => setIsDiscoveryModalOpen(false)}
					onComplete={handleDiscoveryComplete}
					token={token}
					existingKeys={lookupKeys}
				/>
			)}
		</div>
	);
};

LookupKeysManager.propTypes = {};

export default LookupKeysManager;
